import React, { useState, useEffect } from 'react';
import { AlertCircle, CheckCircle, Clock } from 'lucide-react';
import axios from 'axios';

export function AttendanceCheckPopup({ appointmentId, patientId, doctorName, onClose }) {
  const [status, setStatus] = useState('waiting'); // waiting, submitted, error
  const [countdown, setCountdown] = useState(15 * 60); // 15 minutes in seconds
  const [notificationSent, setNotificationSent] = useState(false);

  const API = process.env.REACT_APP_BACKEND_URL || 'http://upchaarq.com';

  // 15 minute countdown
  useEffect(() => {
    if (countdown <= 0) {
      // Auto-mark as absent
      handleTimeout();
      return;
    }

    const timer = setInterval(() => {
      setCountdown(prev => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [countdown]);

  // Send notification after 30 seconds
  useEffect(() => {
    const notifyTimer = setTimeout(() => {
      if (status === 'waiting' && !notificationSent) {
        sendNotification();
        setNotificationSent(true);
      }
    }, 30000);

    return () => clearTimeout(notifyTimer);
  }, [status, notificationSent]);

  const sendNotification = async () => {
    try {
      await axios.post(`${API}/api/attendance/send-check/${appointmentId}`, {
        patient_id: patientId
      });
      console.log('Notification sent');
    } catch (e) {
      console.error('Notification error:', e);
    }
  };

  const handleTimeout = async () => {
    // Auto-mark as absent
    try {
      await axios.post(`${API}/api/attendance/mark/${appointmentId}`, {
        status: 'absent'
      });
      setStatus('auto_marked');
    } catch (e) {
      console.error('Timeout error:', e);
    }
  };

  const handleYes = async () => {
    setStatus('submitted');
    try {
      const response = await axios.post(`${API}/api/attendance/mark/${appointmentId}`, {
        status: 'present'
      });

      if (response.data.success) {
        setStatus('success');
        setTimeout(() => onClose(), 2000);
      }
    } catch (e) {
      setStatus('error');
      console.error('Error marking attendance:', e);
    }
  };

  const handleNo = async () => {
    setStatus('submitted');
    try {
      const response = await axios.post(`${API}/api/attendance/mark/${appointmentId}`, {
        status: 'absent'
      });

      if (response.data.success) {
        setStatus('no_selected');
        setTimeout(() => onClose(), 2000);
      }
    } catch (e) {
      setStatus('error');
      console.error('Error:', e);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // SUCCESS STATE
  if (status === 'success') {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-md text-center animate-bounce">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-green-700 mb-2">शुक्रिया! (Thank You!)</h2>
          <p className="text-slate-600">आपकी उपस्थिति दर्ज हो गई है</p>
          <p className="text-sm text-slate-500 mt-2">डॉक्टर आपको बुलाएंगे</p>
        </div>
      </div>
    );
  }

  // NO SELECTED STATE
  if (status === 'no_selected') {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-md text-center">
          <AlertCircle className="w-16 h-16 text-blue-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-blue-700 mb-2">ठीक है!</h2>
          <p className="text-slate-600">जैसे ही आप hospital में आ जाएं</p>
          <p className="text-slate-600">नीचे "Hospital आ गया" button दबा देना</p>
        </div>
      </div>
    );
  }

  // AUTO MARKED STATE
  if (status === 'auto_marked') {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-md text-center">
          <AlertCircle className="w-16 h-16 text-orange-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-orange-700 mb-2">समय समाप्त!</h2>
          <p className="text-slate-600">आपको 15 मिनट के लिए अनुपस्थित माना गया</p>
          <p className="text-slate-600">जब आप आ जाएं तो Dashboard से confirm कर दें</p>
        </div>
      </div>
    );
  }

  // MAIN POPUP
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl p-8 max-w-md w-full shadow-2xl">
        {/* Header */}
        <div className="text-center mb-6">
          <AlertCircle className="w-12 h-12 text-blue-500 mx-auto mb-3" />
          <h2 className="text-2xl font-bold text-slate-900">
            क्या आप hospital में हो?
          </h2>
          <p className="text-slate-600 mt-2">
            {doctorName} आपका अगला इलाज करेंगे
          </p>
        </div>

        {/* Timer */}
        <div className="bg-blue-50 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <span className="text-slate-700 font-medium">समय बाकी:</span>
            <div className="text-2xl font-mono font-bold text-blue-600">
              {formatTime(countdown)}
            </div>
          </div>
          {notificationSent && (
            <p className="text-xs text-blue-600 mt-2">📱 Notification भेजी जा चुकी है</p>
          )}
        </div>

        {/* Message */}
        <p className="text-center text-sm text-slate-600 mb-6">
          अगर आप hospital में हैं तो <span className="font-bold">YES</span> दबाएं
          <br />
          अगर अभी नहीं आए हैं तो <span className="font-bold">NO</span> दबाएं
        </p>

        {/* Buttons */}
        <div className="grid grid-cols-2 gap-4">
          <button
            onClick={handleYes}
            disabled={status === 'submitted'}
            className="bg-green-500 hover:bg-green-600 disabled:bg-gray-400 text-white font-bold py-3 rounded-lg transition"
          >
            {status === 'submitted' ? 'प्रतीक्षा...' : 'हाँ (YES)'}
          </button>
          <button
            onClick={handleNo}
            disabled={status === 'submitted'}
            className="bg-orange-500 hover:bg-orange-600 disabled:bg-gray-400 text-white font-bold py-3 rounded-lg transition"
          >
            {status === 'submitted' ? 'प्रतीक्षा...' : 'नहीं (NO)'}
          </button>
        </div>

        {/* Error State */}
        {status === 'error' && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded p-3">
            <p className="text-red-600 text-sm text-center">कोई error आई है। फिर से कोशिश करें।</p>
          </div>
        )}
      </div>
    </div>
  );
}
